import React, {
  memo,
  ReactElement,
  createRef,
  useEffect,
  useState,
  Dispatch,
  SetStateAction
} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {css} from '@emotion/core'
import ReCAPTCHA from 'react-google-recaptcha-enterprise'
import {useHistory} from 'react-router'
import {toggle} from 'opticks/lib/optimizely'

import {
  cleanUpLoginErrors,
  cleanUpLoginStatus,
  hidePasswordlessLogin,
  setResendMessage,
  showPasswordlessLogin
} from '@daedalus/core/src/auth/modules/slice'

import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@daedalus/core/src/auth/modules/actions'

import {
  AuthContextType,
  AuthenticationFlows,
  ShowPasswordlessLoginPayload,
  TrackUserSignInClickSourceComponent
} from '@daedalus/core/src/auth/types/Auth'

import {
  getAuthenticationFlow,
  getChannelsAttempts,
  getEnabledChannelAvailable,
  getEnabledChannels,
  getIsResendingPasswordlessMessage,
  getSelectedChannel,
  getUserPhoneNumber,
  hasAuth0GenericErrorResponse,
  hasAuth0PhoneNumberValidationError,
  hasPasswordlessLoginError,
  isGettingUserPhoneNumber,
  isPasswordlessLoginVisible,
  getCognitoSession,
  selectIsAuthenticated,
  selectIsLoading
} from '@daedalus/core/src/auth/modules/selectors'

import {
  makeOtpLogin,
  makeSocialLogin,
  sendMessage,
  SocialConnections,
  startLoginSignUp,
  answerCustomChallenge,
  AuthToolProvider
} from '@daedalus/core/src/auth/modules/thunks'

import {
  EnabledMessageChannelsType,
  MessageChannelsType,
  UserPhoneNumberType
} from '@daedalus/core/src/auth/types/Passwordless'

import {BrandType} from '@daedalus/core/src/brand/types'

import ContentWrapper from '../../../designSystem/helpers/ContentWrapper'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@daedalus/core/src/analytics/types/Events'
import {
  useAuthConnection,
  UseCognitoType,
  useLogIn
} from '@daedalus/core/src/auth/modules/useCognito'
import {useAuth} from '@daedalus/core/src/auth/components/AuthProvider'
import {MOBILE_HEADER_HEIGHT} from '../../general/CompactHeader/constants'

const messageMaximumAttempts = 3

export type HandleDesktopCodeSubmitProps = {
  codeOne: string
  codeTwo: string
  codeThree: string
  codeFour: string
}

export type HandleMobileCodeSubmitProps = {
  code: string
}

export type SetSelectedChannel = Dispatch<SetStateAction<MessageChannelsType>>

export type ChildrenProps = {
  isOpen: boolean
  authenticationFlow: AuthenticationFlows
  userPhoneNumber: string
  hasGenericSendErrorResponse: boolean
  hasAuthenticationError: boolean
  hasEnabledChannelAvailable: boolean
  isResendMessageVisible: boolean
  userSelectedChannel: MessageChannelsType
  enabledChannels: EnabledMessageChannelsType
  hasPhoneNumberValidationErrorResponse: boolean
  isAttemptsByChannelMessageVisible: boolean
  messagesAttemptsHistory: Record<MessageChannelsType, number>
  messageMaximumAttempts: number
  isInitialStep: boolean
  sendPasswordlessMessage: ({
    phoneNumber,
    selectedChannel
  }: SubmitPasswordlessType) => void
  handleCloseOverlay: () => void
  handleCodeSubmit: (code: string) => void
  handleTryAgainAfterError: () => void
  handleReSendSms: () => void
  handleLoginClick: ({
    sourceComponent,
    flow
  }: {
    sourceComponent: ShowPasswordlessLoginPayload['sourceComponent']
    flow: AuthenticationFlows
  }) => void
  handleSocialLoginClick: (
    socialConnection: SocialConnections,
    sourceComponent?: ShowPasswordlessLoginPayload['sourceComponent']
  ) => void
  recaptchaRef: React.Ref<ReCAPTCHA>
  selectedChannel: MessageChannelsType
  setSelectedChannel: SetSelectedChannel
}

interface PasswordlessLoginPropsType {
  languageCode: string
  anonymousId: string
  brand: BrandType
  children: (props: ChildrenProps) => ReactElement<ChildrenProps>
}

type SubmitPasswordlessType = {
  phoneNumber: UserPhoneNumberType
  selectedChannel: MessageChannelsType
}

export const OverlayWrapper = styled(ContentWrapper)(
  ({theme}) => css`
    padding-top: ${theme.layout.spacing.s800}px;
    display: flex;
    flex-direction: column;
    flex: 1;
    height: calc(100% - ${MOBILE_HEADER_HEIGHT}px);
  `
)

export const PopUpWrapper = styled('div')(
  ({theme}) => css`
    flex: 0 1 auto;
    position: relative;
    padding: ${theme.layout.spacing.s300}px;
    overflow: hidden;
  `
)

const useToggleAuth = () => {
  const auth0Values = useAuth()
  const logIn = useLogIn()
  const authConnection = useAuthConnection()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isLoading = useSelector(selectIsLoading)

  return toggle('f54ef0b3-cognito-auth', auth0Values, {
    logIn,
    authConnection,
    isAuthenticated,
    isLoading
  }) as
    | Partial<AuthContextType>
    | Pick<
        UseCognitoType,
        'logIn' | 'authConnection' | 'isAuthenticated' | 'isLoading'
      >
}

const PasswordlessLogin = ({
  languageCode,
  anonymousId,
  brand,
  children
}: PasswordlessLoginPropsType) => {
  const dispatch = useDispatch()
  const {logIn, authConnection, isAuthenticated, isLoading} = useToggleAuth()

  const recaptchaRef = createRef<ReCAPTCHA>()
  const history = useHistory()

  const {
    location: {search}
  } = history

  // Selectors
  const isOpen = useSelector(isPasswordlessLoginVisible)
  const authenticationFlow = useSelector(getAuthenticationFlow)
  const enabledChannels = useSelector(getEnabledChannels)
  const userPhoneNumber = useSelector(getUserPhoneNumber)
  const hasGenericSendErrorResponse = useSelector(hasAuth0GenericErrorResponse)
  const hasAuthenticationError = useSelector(hasPasswordlessLoginError)
  const hasEnabledChannelAvailable = useSelector(getEnabledChannelAvailable)
  const isResendMessageVisible = useSelector(getIsResendingPasswordlessMessage)
  const userSelectedChannel = useSelector(getSelectedChannel)
  const hasPhoneNumberValidationErrorResponse = useSelector(
    hasAuth0PhoneNumberValidationError
  )
  const isInitialStep = useSelector(isGettingUserPhoneNumber)
  const cognitoSession = useSelector(getCognitoSession)
  const messagesAttemptsHistory = useSelector(getChannelsAttempts)
  const [selectedChannel, setSelectedChannel] =
    useState<MessageChannelsType>('sms')

  const isAttemptsByChannelMessageVisible =
    isResendMessageVisible &&
    messagesAttemptsHistory[userSelectedChannel] >= messageMaximumAttempts

  // Action Dispatchers
  const sendPasswordlessMessage = async ({
    phoneNumber,
    selectedChannel
  }: SubmitPasswordlessType) => {
    const token = await recaptchaRef.current.executeAsync()

    dispatch(cleanUpLoginErrors())

    toggle(
      'f54ef0b3-cognito-auth',
      () => {
        dispatch(
          sendMessage({
            phoneNumber,
            languageCode,
            selectedChannel,
            brandCode: brand.code,
            token,
            authConnection
          })
        )
      },
      () => {
        dispatch(
          startLoginSignUp({
            phoneNumber,
            languageCode,
            selectedChannel,
            brandCode: brand.code,
            token
          })
        )
      }
    )
  }

  const handleCloseOverlay = () => {
    dispatch(cleanUpLoginErrors())
    dispatch(cleanUpLoginStatus())
    dispatch(hidePasswordlessLogin())
  }

  const handleCodeSubmit = (code: string) => {
    toggle(
      'f54ef0b3-cognito-auth',
      () => {
        dispatch(
          makeOtpLogin({
            phoneNumber: userPhoneNumber,
            code,
            authConnection,
            selectedChannel: userSelectedChannel
          })
        )
      },
      () => {
        dispatch(
          answerCustomChallenge({
            user: cognitoSession,
            code,
            phoneNumber: userPhoneNumber,
            selectedChannel: userSelectedChannel
          })
        )
      }
    )
  }

  const handleTryAgainAfterError = () => {
    dispatch(cleanUpLoginErrors())
    dispatch(setResendMessage())

    if (!isOpen) {
      dispatch(showPasswordlessLogin())
    }
  }

  const handleReSendSms = () => {
    dispatch(cleanUpLoginErrors())
    dispatch(setResendMessage())
  }

  const handleLoginClick = ({
    sourceComponent,
    flow
  }: {
    sourceComponent: ShowPasswordlessLoginPayload['sourceComponent']
    flow: AuthenticationFlows
  }) =>
    logIn({
      sourceComponent,
      anonymousId,
      flow
    })

  const handleSocialLoginClick = (socialConnection: SocialConnections) => {
    const authToolProvider = toggle(
      'f54ef0b3-cognito-auth',
      AuthToolProvider.Auth0,
      AuthToolProvider.Cognito
    ) as AuthToolProvider

    setTimeout(
      () =>
        dispatch(
          makeSocialLogin({
            connection: socialConnection,
            authConnection,
            authToolProvider
          })
        ),
      TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY
    )
  }

  // This makes ReCAPTCHA uses recaptcha.net instead of google.com and set only one cookie _GRECAPTCHA
  useEffect(() => {
    // @ts-expect-error
    // eslint-disable-next-line fp/no-mutation
    window.recaptchaOptions = {
      useRecaptchaNet: true
    }
  }, [])

  useEffect(() => {
    const susiQueryParam = 'susi'
    const queryParams = new URLSearchParams(location.search)
    const hasSusiInUrl = queryParams.get(susiQueryParam) === 'true'

    if (!isLoading && !isAuthenticated && hasSusiInUrl) {
      const component = TrackUserSignInClickSourceComponent.QueryString

      logIn({
        sourceComponent: component,
        flow: AuthenticationFlows.SignUp,
        anonymousId
      })
      queryParams.set(susiQueryParam, 'false')
      return history.replace({search: queryParams.toString()})
    }

    if (isAuthenticated && hasSusiInUrl) {
      queryParams.delete(susiQueryParam)
      history.replace({search: queryParams.toString()})
    }
  }, [search, isLoading, isAuthenticated, anonymousId, history, logIn])

  useEffect(() => {
    if (isOpen) {
      const EntityMap = {
        [AuthenticationFlows.UnlockOverlay]: Entity.UnlockOverlay,
        [AuthenticationFlows.Login]: Entity.LoginOverlay,
        [AuthenticationFlows.SignUp]: Entity.SignupOverlay
      }

      dispatch(
        trackEvent({
          category: Category.System,
          entity: EntityMap[authenticationFlow],
          action: Action.Displayed,
          team: Team.Retention
        })
      )
    }
  }, [isOpen, dispatch, authenticationFlow])

  return children({
    isOpen,
    authenticationFlow,
    enabledChannels,
    userPhoneNumber,
    hasGenericSendErrorResponse,
    hasAuthenticationError,
    hasEnabledChannelAvailable,
    isResendMessageVisible,
    userSelectedChannel,
    hasPhoneNumberValidationErrorResponse,
    isInitialStep,
    isAttemptsByChannelMessageVisible,
    messagesAttemptsHistory,
    messageMaximumAttempts,
    sendPasswordlessMessage,
    handleCloseOverlay,
    handleCodeSubmit,
    handleTryAgainAfterError,
    handleLoginClick,
    handleReSendSms,
    handleSocialLoginClick,
    recaptchaRef,
    selectedChannel,
    setSelectedChannel
  })
}

export default memo<PasswordlessLoginPropsType>(PasswordlessLogin)
